import React from 'react';
import { motion } from 'framer-motion';

function ResearchAnalytics() {
  const researchAreas = [
    {
      title: 'Market Research',
      description: 'Comprehensive market analysis covering global equity, fixed income, and alternative investment markets.',
      features: ['Macroeconomic Analysis', 'Sector Research', 'Market Trends', 'Investment Themes']
    },
    {
      title: 'Sector Analysis',
      description: 'Deep-dive sector research providing insights into industry dynamics and investment opportunities.',
      features: ['Industry Analysis', 'Competitive Landscape', 'Growth Drivers', 'Risk Factors']
    },
    {
      title: 'Risk Modeling',
      description: 'Advanced quantitative risk models to assess portfolio risk and optimize risk-adjusted returns.',
      features: ['VaR Analysis', 'Stress Testing', 'Scenario Analysis', 'Risk Attribution']
    },
    {
      title: 'Performance Analytics',
      description: 'Sophisticated performance analytics to evaluate investment strategies and benchmark comparisons.',
      features: ['Return Attribution', 'Risk-Adjusted Metrics', 'Benchmark Analysis', 'Performance Reporting']
    }
  ];

  const methodologies = [
    {
      step: '01',
      title: 'Data Collection',
      description: 'Comprehensive data gathering from multiple sources including market data, economic indicators, and alternative datasets.'
    },
    {
      step: '02',
      title: 'Analysis & Modeling',
      description: 'Advanced quantitative analysis using proprietary models and industry-standard methodologies.'
    },
    {
      step: '03',
      title: 'Insight Generation',
      description: 'Translation of complex data into actionable investment insights and recommendations.'
    },
    {
      step: '04',
      title: 'Delivery & Reporting',
      description: 'Clear presentation of findings through reports, presentations, and interactive dashboards.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img 
          src="/images/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg"
          alt="Research & Analytics"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-[-0.02em] mb-6">
              <span className="block font-light">Research &</span>
              <span className="block font-medium text-[#fbbf24]">Analytics</span>
            </h1>
            <p className="text-white/90 text-xl sm:text-2xl font-light leading-relaxed max-w-3xl mx-auto">
              Data-driven insights for informed investment decisions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Research Areas Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Comprehensive Research Coverage</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              Our research team provides deep insights across all major asset classes and investment themes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {researchAreas.map((area, index) => (
              <div
                key={area.title}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 150}`}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-8 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-full bg-[#0056d7] flex items-center justify-center shadow-md mb-6">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                    <path d="M3 3v18h18" />
                    <path d="M7 15l4-4 3 3 4-6" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{area.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{area.description}</p>
                
                <ul className="space-y-3">
                  {area.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-[#0056d7] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Our Research Methodology</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              A systematic approach to generating actionable investment insights
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodologies.map((step, index) => (
              <div
                key={step.step}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 100}`}
                className="relative p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105"
              >
                <div className="absolute -top-4 left-8 w-12 h-12 bg-[#0056d7] text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Expert Research Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              Our research team combines deep market knowledge with advanced analytical capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Dr. Sarah Chen',
                title: 'Head of Research',
                specialization: 'Macroeconomic Analysis',
                image: '/images/people/team-4-female.png'
              },
              {
                name: 'Michael Rodriguez',
                title: 'Senior Quantitative Analyst',
                specialization: 'Risk Modeling',
                image: '/images/people/user-4-male.png'
              },
              {
                name: 'Emily Thompson',
                title: 'Sector Research Director',
                specialization: 'Equity Research',
                image: '/images/people/team-5-female.png'
              }
            ].map((member, index) => (
              <div
                key={member.name}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 150}`}
                className="text-center p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 object-cover rounded-full mx-auto mb-6 shadow-lg"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-[#0056d7] font-medium mb-4">{member.title}</p>
                <p className="text-gray-600 text-sm">{member.specialization}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0056d7] via-[#003a9e] to-[#002a73] text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-light text-white mb-6">Access Our Research</h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Get insights from our research team and discover how our analytics can enhance your investment decisions
            </p>
            <motion.a
              href="/contact"
              className="inline-block px-12 py-6 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-medium text-xl transition-all duration-500 shadow-2xl hover:shadow-amber-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Request Research Access
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ResearchAnalytics;
